import { useCallback, useState } from "react";
import * as React from "react";

import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { Anchor, Text, Group } from "@mantine/core";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";

import { EditDetails } from "~/features/tokens/sections/ManageToken/modals/EditDetails/EditDetails";
import { GetManageTokenCollectionQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

import { PropertyBox } from "./PropertyBox";

export const DescriptionProperty = ({
  description,
  collection,
  refetch,
}: {
  description: string;
  collection?: GetManageTokenCollectionQuery["getCollection"];
  refetch?: () => void;
}) => {
  const CHAR_LIMIT = 250;
  const [displayedDescription, setDisplayedDescription] = useState<string>(
    description.substring(0, CHAR_LIMIT)
  );
  const [showSeeMore, setShowSeeMore] = useState<boolean>(
    description.length > CHAR_LIMIT
  );
  const [showSeeLess, setShowSeeLess] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const handleOnClickSeeMore = useCallback(() => {
    setDisplayedDescription(description);
    setShowSeeLess(true);
    setShowSeeMore(false);
  }, [description]);

  const handleOnClickSeeLess = useCallback(() => {
    setDisplayedDescription(description.substring(0, CHAR_LIMIT));
    setShowSeeLess(false);
    setShowSeeMore(true);
  }, [description]);

  const enableEdit = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_EDIT_COLLECTION_DETAILS
  );

  return (
    <>
      <PropertyBox
        label="Description"
        value={
          <Group position="apart" w="100%" align="start" noWrap>
            <Text size="sm">
              <ReactMarkdown
                remarkPlugins={[gfm]}
                components={{
                  a: (props) => (
                    <Anchor
                      {...props}
                      sx={(theme) => ({
                        fontSize: theme.fontSizes.sm,
                        textDecoration: "underline",
                        wordBreak: "break-word",
                      })}
                    />
                  ),
                }}
              >
                {displayedDescription}
              </ReactMarkdown>
              {showSeeMore ? (
                <Text size="sm">
                  <Anchor
                    size="sm"
                    fw={WEIGHT_BOLD}
                    onClick={handleOnClickSeeMore}
                  >
                    See more
                  </Anchor>
                </Text>
              ) : (
                ""
              )}
              {showSeeLess ? (
                <Text size="sm">
                  <Anchor
                    size="sm"
                    fw={WEIGHT_BOLD}
                    onClick={handleOnClickSeeLess}
                  >
                    See less
                  </Anchor>
                </Text>
              ) : (
                ""
              )}
            </Text>
            {enableEdit && collection && (
              <Anchor
                w="auto"
                underline={false}
                onClick={() => setShowEditModal(true)}
              >
                <Text size="sm" fw={WEIGHT_BOLD}>
                  Edit
                </Text>
              </Anchor>
            )}
          </Group>
        }
      />
      {collection && (
        <EditDetails
          collection={collection}
          isOpened={showEditModal}
          onClose={() => setShowEditModal(false)}
          refetch={refetch}
        />
      )}
    </>
  );
};
